/**
 * GlobalSearchBar — universal search with debounced suggestions.
 *
 * - Calls GET /api/search?q=&lat=&lng= (uses one-shot geolocation when allowed
 *   so the store hits are distance-sorted)
 * - Shows two grouped sections in the dropdown: Products / Stores
 * - Click a product → /store/<store_id> ; click a store → /store/<store_id>
 *   (room to evolve into a dedicated product detail page later)
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Store as StoreIcon, Package, UtensilsCrossed, Plus } from 'lucide-react';
import { discoveryAPI, GlobalSearchResponse } from '../api/discovery';
import { cartAPI } from '../api/cart';
import { useAuthStore } from '../stores/authStore';
import toast from 'react-hot-toast';

interface Props {
  placeholder?: string;
  compact?: boolean;
}

const GlobalSearchBar: React.FC<Props> = ({
  placeholder = 'Search products & stores…',
  compact = false,
}) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<GlobalSearchResponse | null>(null);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const debRef = useRef<any>();
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [addingId, setAddingId] = useState<number | null>(null);

  // One-shot, silent geolocation request to bias store ranking.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => { /* user denied — server still returns alpha-sorted stores */ },
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 5 * 60_000 }
    );
  }, []);

  // Outside click → close
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    debRef.current = setTimeout(async () => {
      try {
        const r = await discoveryAPI.search({
          q: q.trim(),
          lat: coords?.lat,
          lng: coords?.lng,
          limit: 6,
        });
        setResults(r);
        setOpen(true);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
  }, [q, coords]);

  const goProduct = (p: { store_id: number; id: number; name: string }) => {
    setOpen(false);
    setQ('');
    // Route to the dedicated product detail page now that one exists.
    navigate(`/product/${p.id}`);
  };
  const goStore = (storeId: number) => {
    setOpen(false);
    setQ('');
    navigate(`/store/${storeId}`);
  };
  const goDish = (m: { store_id: number; id: number; name: string }) => {
    setOpen(false);
    setQ('');
    navigate(`/restaurant/${m.store_id}?focus=${m.id}&search=${encodeURIComponent(m.name)}`);
  };

  const handleAddProduct = async (
    e: React.MouseEvent,
    p: { id: number; store_id: number; name: string },
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isAuthenticated) {
      setOpen(false);
      navigate('/login');
      return;
    }
    try {
      setAddingId(p.id);
      await cartAPI.addToCart(p.store_id, p.id, 1);
      toast.success(`Added ${p.name}`, { duration: 1500 });
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Could not add to cart');
    } finally {
      setAddingId(null);
    }
  };

  const hasAny = !!results && (
    results.products.length > 0 ||
    results.stores.length > 0 ||
    (results.menu_items?.length ?? 0) > 0
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: compact ? 240 : '100%', maxWidth: 520 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--pm-color-surface)',
          border: '1px solid var(--pm-color-border)',
          borderRadius: 12,
          padding: '8px 12px',
          boxShadow: 'var(--pm-shadow-card)',
        }}
      >
        <Search size={16} color="var(--pm-color-text-muted)" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results && setOpen(true)}
          placeholder={placeholder}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontSize: 14,
            color: 'var(--pm-color-text)',
          }}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(''); setResults(null); }}
            aria-label="Clear search"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pm-color-text-muted)', display: 'flex' }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1050,
            marginTop: 6,
            background: 'var(--pm-color-surface)',
            border: '1px solid var(--pm-color-border)',
            borderRadius: 12,
            boxShadow: 'var(--pm-shadow-card-hover)',
            maxHeight: 440,
            overflowY: 'auto',
          }}
        >
          {loading && (
            <div style={{ padding: 14, fontSize: 13, color: 'var(--pm-color-text-muted)' }}>Searching…</div>
          )}
          {!loading && !hasAny && (
            <div style={{ padding: 14, fontSize: 13, color: 'var(--pm-color-text-muted)' }}>
              No results for “{q}”.
            </div>
          )}

          {results && results.products.length > 0 && (
            <div>
              <div style={sectionHeader}>
                <Package size={12} /> Products
              </div>
              {results.products.map((p) => (
                <div
                  key={`p-${p.id}`}
                  onMouseDown={() => goProduct(p)}
                  style={row}
                >
                  {p.image_url ? (
                    <img src={p.image_url} alt="" style={thumb} onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
                  ) : (
                    <div style={{ ...thumb, background: 'var(--pm-color-surface-2)' }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={titleStyle}>{p.name}</div>
                    <div style={subtitleStyle}>
                      {p.store_name ? <>at {p.store_name}</> : null}
                      {p.unit ? <> · {p.unit}</> : null}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--pm-color-text)' }}>₹{p.price.toFixed(0)}</div>
                  <button
                    type="button"
                    title="Add to cart"
                    onMouseDown={(e) => handleAddProduct(e, p)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={addingId === p.id}
                    style={addBtn}
                  >
                    <Plus size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {results && results.stores.length > 0 && (
            <div>
              <div style={sectionHeader}>
                <StoreIcon size={12} /> Stores
              </div>
              {results.stores.map((s) => (
                <div
                  key={`s-${s.id}`}
                  onMouseDown={() => goStore(s.id)}
                  style={row}
                >
                  {s.logo_url ? (
                    <img src={s.logo_url} alt="" style={thumb} onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
                  ) : (
                    <div style={{ ...thumb, background: 'var(--pm-color-primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                      {s.business_name.slice(0, 1)}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={titleStyle}>{s.business_name}</div>
                    <div style={subtitleStyle}>
                      {s.city || s.address || ''}
                      {s.distance_km != null ? <> · {s.distance_km} km away</> : null}
                    </div>
                  </div>
                  {!s.is_open && <span style={closedPill}>Closed</span>}
                </div>
              ))}
            </div>
          )}

          {results && (results.menu_items?.length ?? 0) > 0 && (
            <div>
              <div style={sectionHeader}>
                <UtensilsCrossed size={12} /> Dishes
              </div>
              {results.menu_items.map((m) => (
                <div
                  key={`m-${m.id}`}
                  onMouseDown={() => goDish(m)}
                  style={row}
                >
                  {m.image_url ? (
                    <img src={m.image_url} alt="" style={thumb} onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
                  ) : (
                    <div style={{ ...thumb, background: 'var(--pm-color-accent)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                      {m.name.slice(0, 1)}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={titleStyle}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 10, height: 10, borderRadius: 2, marginRight: 6,
                          border: `2px solid ${m.is_veg ? '#16A34A' : '#DC2626'}`,
                        }}
                      />
                      {m.name}
                    </div>
                    <div style={subtitleStyle}>
                      {m.store_name ? <>at {m.store_name}</> : null}
                      {m.category_name ? <> · {m.category_name}</> : null}
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, color: 'var(--pm-color-text)' }}>₹{m.price.toFixed(0)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const sectionHeader: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 12px 4px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'var(--pm-color-text-muted)',
};
const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  cursor: 'pointer',
  borderTop: '1px solid var(--pm-color-border)',
};
const thumb: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  objectFit: 'cover',
  flexShrink: 0,
};
const titleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--pm-color-text)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const subtitleStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--pm-color-text-muted)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const closedPill: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  background: '#FEE2E2',
  color: '#991B1B',
  padding: '3px 6px',
  borderRadius: 6,
};
const addBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: 8,
  border: '1px solid var(--pm-color-primary, #16A34A)',
  background: 'var(--pm-color-primary, #16A34A)',
  color: '#fff',
  cursor: 'pointer',
  flexShrink: 0,
  marginLeft: 6,
};

export default GlobalSearchBar;

